















































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'

import { FormItem } from './components/FormItem'
import { DeliveryForm } from './components/DeliveryForm'
import { CreditCardForm, CreditCardValue } from './components/CreditCardForm'
import {
  InsuranceUploadForm,
  InsuranceUploadValue
} from './components/InsuranceUploadForm'
import ConfirmAnswer from './components/FormItem/components/ConfirmAnswer/ConfirmAnswer.vue'
import {
  QuestionImageType,
  PostConsultationQuestionType,
  PostConsultationRequest
} from './api'
import {
  Address,
  Consulation,
  FincodeCreditcardRequest,
  FincodeCreditcardResponse
} from '@/interface/api'
import { FINCODE_API_KEY } from '@/utils/constants'

type ChildValue = string | string[] | File[] | null

type AnswerValueType = Record<
  number | string,
  {
    value: string | string[] | File[] | null
    childValues?: {
      [key: number]: Array<{
        childValue: ChildValue
      }>
    }
    hasError?: boolean
    hasChildError?: boolean
  }
>

@Component({
  components: {
    FormItem,
    DeliveryForm,
    CreditCardForm,
    ConfirmAnswer,
    InsuranceUploadForm
  }
})
export default class ConsultationForm extends Vue {
  @Prop({ default: '' }) private medicalBusinessId!: string
  @Prop({ default: '' }) private loading!: boolean
  @Prop({ default: () => null }) private consultation!: Consulation
  @Prop({ default: false }) private isMultilingualEnabled!: boolean

  private mode = 'consultation'
  private answerValues: AnswerValueType = {}
  private deliveryAddressValue: Address = {
    postal_code: '',
    prefecture: '',
    city: '',
    other1: '',
    other2: ''
  }

  private isConfirm = false

  private creditCardValue: CreditCardValue = {
    creditNumber: '',
    expirationMonth: '',
    expirationYear: '',
    securityCode: '',
    token: '',
    token_expire_date: null,
    req_card_number: '',
    card_expire: '',
    is_default: 1,
    settlement_company_name: '',
    brand: ''
  }

  private insuranceValue: InsuranceUploadValue = {
    imageType: 'myna',
    firstImage: [],
    secondImage: []
  }

  private get title() {
    return this.mode === 'consultation'
      ? '事前問診回答'
      : this.mode === 'delivery'
      ? '配送先住所入力'
      : this.mode === 'credit-card'
      ? 'クレジットカード入力'
      : this.mode === 'insurance'
      ? '保険証/資格確認証入力'
      : '回答内容を確認する'
  }

  private get questions() {
    return this.consultation?.questions || []
  }

  private get askAddress() {
    return Boolean(this.consultation?.ask_address)
  }

  private get askCreditCardInformation() {
    return Boolean(this.consultation?.ask_credit_card_information)
  }

  private get askInsuranceCardInformation() {
    return Boolean(this.consultation?.is_insurance_approval)
  }

  private get canSkipCreditCard() {
    return !this.consultation?.is_required_credit_card
  }

  private get canSkipInsurance() {
    return !this.consultation?.is_required_insurance
  }

  private get nextButtonText() {
    if (this.mode === 'consultation') {
      if (this.askAddress) {
        return '配送先住所入力に進む'
      }
      if (this.askCreditCardInformation) {
        return 'クレジットカード入力に進む'
      }
      if (this.askInsuranceCardInformation) {
        return '保険証/資格確認証入力に進む'
      }
      return '回答内容を確認する'
    }
    if (this.mode === 'delivery') {
      if (this.askCreditCardInformation) {
        return 'クレジットカード入力に進む'
      }
      if (this.askInsuranceCardInformation) {
        return '保険証/資格確認証入力に進む'
      }
      return '回答内容を確認する'
    }
    if (this.mode === 'credit-card') {
      if (this.askInsuranceCardInformation) {
        return '保険証/資格確認証入力に進む'
      }
      return '回答内容を確認する'
    }
    if (this.mode === 'insurance') {
      return '回答内容を確認する'
    }
    return '回答内容を確認する'
  }

  private get backButtonText() {
    if (this.mode === 'consultation') {
      return '予約情報を確認する'
    } else if (this.mode === 'delivery') {
      return '事前問診回答に戻る'
    } else if (this.mode === 'credit-card') {
      if (this.askAddress) {
        return '配送先住所入力に戻る'
      }
      return '事前問診回答に戻る'
    } else if (this.mode === 'insurance') {
      if (this.askCreditCardInformation) {
        return 'クレジットカード入力に戻る'
      }
      if (this.askAddress) {
        return '配送先住所入力に戻る'
      }
      return '事前問診回答に戻る'
    }
    return '事前問診回答に戻る'
  }

  private get skipButtonText() {
    if (this.mode === 'credit-card') {
      return 'クレジットカードをあとで登録する'
    } else if (this.mode === 'insurance') {
      return '保険証/資格確認証をあとで登録する'
    }
    return ''
  }

  @Watch('consultation', { immediate: true })
  async onConsultationChange() {
    this.answerValues = this.consultation.questions.reduce<AnswerValueType>(
      (prev, cur) => {
        const childValues: {
          [key: number]: Array<{
            childValue: ChildValue
          }>
        } = {}
        cur.answer_options.forEach(ao => {
          const aos: Array<{
            childValue: ChildValue
          }> = []
          ao.child_questions.forEach(() => {
            aos.push({ childValue: null })
          })
          childValues[ao.question_answer_option_id] = aos
        })
        prev[cur.id] = { value: null, childValues: childValues }
        return prev
      },
      {}
    )
  }

  private getSrc(file: File) {
    return URL.createObjectURL(file)
  }

  private isTextQuestion(name: string) {
    return name === 'text' || name === 'long_text'
  }

  private getChildQuestions(question: any) {
    const opt = question.answer_options.find(
      (o: any) => o.value === this.answerValues[question.id].value
    )
    return opt?.child_questions || []
  }

  private getChildAnswerValue(question: any, index: number) {
    const opt = question.answer_options.find(
      (o: any) => o.value === this.answerValues[question.id].value
    )
    const optId = opt?.question_answer_option_id || 0
    return (
      this.answerValues[question.id].childValues?.[optId]?.[index]
        ?.childValue || ''
    )
  }

  private getImages(images: ReadonlyArray<QuestionImageType>) {
    return (
      images?.map(image => ({
        id: image.id,
        url: image.question_file_url,
        name: '',
        content_type: image.content_type
      })) || []
    )
  }

  private getOptions(options: ReadonlyArray<any>) {
    return (
      options?.map(option => ({
        id: option.question_answer_option_id,
        name: option.display_name,
        value: option.value,
        image: option.answer_option_image_url,
        children_questions: option.child_questions
          ? this.getConditionChildren(
              option.child_questions,
              option.question_answer_option_id
            )
          : []
      })) || []
    )
  }

  private getConditionChildren(
    childQuestions: ReadonlyArray<any>,
    answerOptionId: number
  ) {
    return (
      childQuestions?.map(childQuestion => ({
        id: childQuestion.id,
        title: childQuestion.title,
        type: childQuestion.question_category_name,
        required: childQuestion.is_required === 1,
        optionId: answerOptionId,
        images: this.getImages(childQuestion.question_files),
        options: this.getChildOptions(childQuestion.answer_options)
      })) || []
    )
  }

  private getChildOptions(options: ReadonlyArray<any>) {
    return (
      options?.map(option => ({
        id: option.question_answer_option_id,
        name: option.display_name,
        value: option.value,
        image: option.answer_option_image_url
      })) || []
    )
  }

  private onChange(
    id: number,
    key: 'value' | 'childValue',
    value: string | string[],
    selectedOptionId: number,
    index: number
  ) {
    if (key === 'value') {
      this.$set(this.answerValues[id], key, value)
    } else {
      const target =
        this.answerValues[id].childValues?.[selectedOptionId]?.[index]
      if (target) {
        this.$set(target, key, value)
      }
    }
  }

  private onClickBack() {
    // モード別にバリデーションと遷移を制御
    if (this.mode === 'consultation') {
      this.$emit('back')
    } else if (this.mode === 'delivery') {
      this.mode = 'consultation'
    } else if (this.mode === 'credit-card') {
      if (this.askAddress) {
        this.mode = 'delivery'
      } else {
        this.mode = 'consultation'
      }
    } else if (this.mode === 'insurance') {
      if (this.askCreditCardInformation) {
        this.mode = 'credit-card'
      } else if (this.askAddress) {
        this.mode = 'delivery'
      } else {
        this.mode = 'consultation'
      }
    } else if (this.mode === 'confirm') {
      this.isConfirm = false
      this.mode = 'consultation'
    }
  }

  private onClickSkip() {
    // モード別にバリデーションと遷移を制御
    if (this.mode === 'credit-card') {
      this.$emit('update:skiped-credit-card', true)
      this.creditCardValue = {
        creditNumber: '',
        expirationMonth: '',
        expirationYear: '',
        securityCode: '',
        token: '',
        token_expire_date: null,
        req_card_number: '',
        card_expire: '',
        is_default: 1,
        settlement_company_name: '',
        brand: ''
      }
      this.proceedAfterCreditCard()
    } else if (this.mode === 'insurance') {
      this.$emit('update:skiped-insurance', true)
      this.insuranceValue = {
        imageType: 'myna',
        firstImage: [],
        secondImage: []
      }
      this.goToConfirm()
    }
  }

  private async onClickNext() {
    // モード別にバリデーションと遷移を制御
    if (this.mode === 'consultation') {
      const requiredErrorId = this.validateRequiredQuestions()
      const childErrorId = this.validateChildRequiredQuestions()
      const firstErrorId =
        requiredErrorId !== null ? requiredErrorId : childErrorId
      if (firstErrorId !== null) {
        this.scrollToQuestion(firstErrorId)
        return
      }
      this.proceedAfterConsultation()
    } else if (this.mode === 'delivery') {
      if (!this.validateDeliveryForm()) return
      this.proceedAfterDelivery()
    } else if (this.mode === 'credit-card') {
      this.$emit('update:skiped-credit-card', false)
      await this.validateCreditCard()
    } else if (this.mode === 'insurance') {
      if (!this.validateInsuranceForm()) return
      this.$emit('update:skiped-insurance', false)
      this.goToConfirm()
    }
  }

  private validateRequiredQuestions(): number | null {
    let errorId: number | null = null
    this.questions.forEach(v => {
      if (v.is_required !== 1) return
      const val = this.answerValues[v.id].value
      if (val) {
        if (!Array.isArray(val) || val.length) {
          this.$set(this.answerValues[v.id], 'hasError', false)
          return
        }
      }
      this.$set(this.answerValues[v.id], 'hasError', true)
      if (errorId === null) errorId = v.id
    })
    return errorId
  }

  private validateChildRequiredQuestions(): number | null {
    let errorId: number | null = null
    this.questions.forEach(v => {
      v.answer_options?.forEach(ao => {
        if (ao.child_questions.length === 0) return
        const parentVal = this.answerValues[v.id].value
        if (ao.value !== parentVal) return
        ao.child_questions?.forEach((cq, i) => {
          if (cq.is_required !== 1) return
          const childValue =
            this.answerValues[v.id].childValues?.[ao.question_answer_option_id][
              i
            ].childValue
          if (childValue) {
            if (Array.isArray(childValue) && childValue.length > 0) return
            if ((childValue as any).length) return
          }
          this.$set(this.answerValues[v.id], 'hasChildError', true)
          if (errorId === null) errorId = v.id
        })
      })
    })
    return errorId
  }

  private validateDeliveryForm(): boolean {
    if (!this.askAddress) return true
    const ok = (this.$refs as any)?.deliveryForm?.validate?.()
    return ok !== false
  }

  private async validateCreditCard() {
    if (!this.askCreditCardInformation) return
    const okCard = (this.$refs as any)?.creditCardForm?.validate?.()
    if (okCard === false) return
    const finecodeRequest: FincodeCreditcardRequest = {
      card_no: this.creditCardValue.creditNumber,
      expire: `${this.creditCardValue.expirationYear}${this.creditCardValue.expirationMonth}`,
      security_code: this.creditCardValue.securityCode
    }

    // グローバル読み込みの Fincode を安全に参照
    const fincodeFactory = (window as any)?.Fincode
    const fincode = fincodeFactory(FINCODE_API_KEY)
    await fincode.tokens(
      finecodeRequest,
      (status: number, response: FincodeCreditcardResponse) => {
        if (status === 200) {
          this.creditCardValue = {
            ...this.creditCardValue,
            token: response.list[0].token,
            token_expire_date: null,
            req_card_number: response.card_no,
            card_expire: `${this.creditCardValue.expirationMonth}/${this.creditCardValue.expirationYear}`,
            settlement_company_name: 'fincode',
            brand: '',
            is_default: 1
          }
          console.log('response', response)
          this.proceedAfterCreditCard()
        }
      }
    )
  }

  private validateInsuranceForm(): boolean {
    console.log('validateInsuranceForm')
    console.log('askInsuranceCardInformation', this.askInsuranceCardInformation)
    if (!this.askInsuranceCardInformation) return true
    console.log('insuranceForm', (this.$refs as any)?.insuranceForm)
    const ok = (this.$refs as any)?.insuranceForm?.validate?.()
    console.log('ok', ok)
    return ok !== false
  }

  private scrollToQuestion(errorId: number | null) {
    if (!errorId) return
    const rect = document
      .getElementById(`${errorId}`)
      ?.getBoundingClientRect() || { top: 0 }
    document.documentElement.scrollTop = rect.top - 50 + window.pageYOffset
  }

  private proceedAfterConsultation() {
    if (this.askAddress) {
      this.mode = 'delivery'
      return
    }
    if (this.askCreditCardInformation) {
      this.mode = 'credit-card'
      return
    }
    if (this.askInsuranceCardInformation) {
      this.mode = 'insurance'
      return
    }
    this.goToConfirm()
  }

  private proceedAfterDelivery() {
    if (this.askCreditCardInformation) {
      this.mode = 'credit-card'
      return
    }
    if (this.askInsuranceCardInformation) {
      this.mode = 'insurance'
      return
    }
    this.goToConfirm()
  }

  private proceedAfterCreditCard() {
    if (this.askInsuranceCardInformation) {
      this.mode = 'insurance'
      return
    }
    this.goToConfirm()
  }

  private goToConfirm() {
    this.mode = 'confirm'
    this.isConfirm = true
    Object.keys(this.answerValues).forEach(v => {
      this.$set(this.answerValues[v], 'hasError', false)
      this.$set(this.answerValues[v], 'hasChildError', false)
    })
    window.scrollTo(0, 0)
  }

  private onClickFix() {
    this.mode = 'consultation'
    this.isConfirm = false
    window.scrollTo(0, 0)
  }

  private async onClickSubmit() {
    let uploadFiles: { file: File; key: string }[] = []
    const data: PostConsultationRequest = {
      clinic_form_id: this.consultation.clinic_form_id,
      questions: Object.keys(this.answerValues).reduce<
        Array<PostConsultationQuestionType>
      >((prev, id) => {
        const { value, childValues } = this.answerValues[id]
        // 親のデータを探索
        const item = this.questions.find(v => v.id.toString() === id)
        if (!item) return prev
        const options = item.answer_options.filter(option => {
          if (
            Array.isArray(value) &&
            value.length !== 0 &&
            typeof value[0] === 'string'
          ) {
            return (value as string[]).includes(option.value)
          }
          return value === option.value
        })
        const isFileUpload = item.question_category_name === 'image'
        const parentImages =
          isFileUpload && value && value.length !== 0
            ? (value as File[]).map((file, index) => {
                return {
                  file,
                  key: `${id}-${index}`
                }
              })
            : []
        uploadFiles = [...uploadFiles, ...parentImages]
        prev.push({
          id: id,
          title: item.title,
          answer_option:
            options.length === 0
              ? '0'
              : options.map(v => v.question_answer_option_id).join(','),
          answer: isFileUpload
            ? (value as File[]) !== null
              ? (value as File[]).map(file => file.name).join(',')
              : ''
            : !options?.length
            ? (value as string)
            : options.map(v => v.display_name).join(','),
          image_keys: isFileUpload ? parentImages.map(i => i.key) : []
        })
        if (!childValues) return prev
        // 子のデータ生成
        options.forEach(option => {
          option.child_questions.forEach((childItem, i) => {
            const childOptions = childItem.answer_options.filter(
              childOption => {
                if (
                  Array.isArray(
                    childValues[option.question_answer_option_id][i].childValue
                  ) &&
                  childValues[option.question_answer_option_id][i]?.childValue
                    ?.length !== 0 &&
                  typeof childValues[option.question_answer_option_id][i]
                    ?.childValue?.[0] === 'string'
                ) {
                  return (
                    childValues[option.question_answer_option_id][i]
                      ?.childValue as string[]
                  ).includes(childOption.value)
                }
                return (
                  childValues[option.question_answer_option_id][i]
                    ?.childValue === childOption.value
                )
              }
            )
            const isChildFileUpload =
              childItem.question_category_name === 'image'
            const childImages =
              isChildFileUpload &&
              childValues[option.question_answer_option_id][i]?.childValue !==
                null
                ? (
                    childValues[option.question_answer_option_id][i]
                      ?.childValue as File[]
                  ).map((file, index) => {
                    return {
                      file,
                      key: `child-${id}-${i}-${index}`
                    }
                  })
                : []
            uploadFiles = [...uploadFiles, ...childImages]
            prev.push({
              id: childItem.id.toString(),
              title: childItem.title,
              answer_option:
                childOptions.length === 0
                  ? '0'
                  : childOptions
                      .map(v => v.question_answer_option_id)
                      .join(','),
              answer: isChildFileUpload
                ? (childValues[option.question_answer_option_id][i]
                    ?.childValue as File[]) !== null
                  ? (
                      childValues[option.question_answer_option_id][i]
                        ?.childValue as File[]
                    )
                      .map(file => file.name)
                      .join(',')
                  : ''
                : !childOptions?.length
                ? (childValues[option.question_answer_option_id][i]
                    ?.childValue as string)
                : childOptions.map(v => v.display_name).join(','),
              image_keys: isChildFileUpload ? childImages.map(i => i.key) : []
            })
          })
        })
        return prev
      }, [])
    }
    // 配送先住所: postal_codeが入力されている場合のみ追加
    if (this.deliveryAddressValue && this.deliveryAddressValue.postal_code) {
      data.delivery_address = this.deliveryAddressValue
    }
    // クレジットカード: tokenが存在する場合のみ追加
    if (this.creditCardValue && this.creditCardValue.token) {
      data.credit_card = {
        req_card_number: this.creditCardValue.req_card_number,
        card_expire: `${this.creditCardValue.expirationYear}${this.creditCardValue.expirationMonth}`,
        token: this.creditCardValue.token,
        token_expire_date: this.creditCardValue.token_expire_date || null,
        is_default: this.creditCardValue.is_default,
        settlement_company_name: '',
        brand: this.creditCardValue.brand
      }
    }
    // 保険証: firstImageにファイルが存在する場合のみ追加
    if (
      this.insuranceValue &&
      this.insuranceValue.firstImage &&
      this.insuranceValue.firstImage.length > 0
    ) {
      data.insurance = {
        image_type: this.insuranceValue.imageType,
        first_image: this.insuranceValue.firstImage[0] || null,
        second_image: this.insuranceValue.secondImage[0] || null
      }
    }
    this.$emit('consultation-submit', data, uploadFiles)
  }
}
